import { useState } from 'react'
import { dueDateCalculatorPath } from 'lib/urls'
import { DatePickerInput } from 'components/forms'
import { isValid, startOfToday, sub } from 'date-fns'
import { useNavigate } from 'react-router-dom-v5-compat'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Button, Dropdown } from 'baby-design'
import classNames from 'classnames'
import { track } from 'lib/analytics'
import css from './DueDateForm.scss'
import {
  Option,
  METHOD_OPTIONS,
  CYCLE_LENGTH_OPTIONS,
  formatDueDate,
  calculateDueDate,
} from './helpers'
import Wave from './components/Wave'
import Background from './components/Background'
import Guides from '../Guides/Guides'
import FrequentlyAskedQuestions from './components/FrequentlyAskedQuestions/FrequentlyAskedQuestions'

const initialDate = sub(startOfToday(), { weeks: 2 })
const ctaText = 'Get my due date'

interface DueDateFormProps {
  posts: Post[]
  longForm?: boolean
}

const DueDateForm: React.FC<DueDateFormProps> = ({
  posts,
  longForm = false,
}) => {
  const navigate = useNavigate()
  const [calculationMethod, setCalculationMethod] = useState<Option>(
    METHOD_OPTIONS[0]
  )
  const [selectedDate, setSelectedDate] = useState(initialDate)
  const [cycleLength, setCycleLength] = useState(
    CYCLE_LENGTH_OPTIONS.find((option) => option.label === '28 days')
  )

  const handleSubmit = () => {
    if (!isValid(selectedDate)) return
    const dueDate = calculateDueDate(selectedDate, calculationMethod)
    if (!dueDate) return

    track.getMyDueDateClicked({
      eventLocation: track.EventLocation.DUE_DATE_CALCULATOR,
      eventCta: ctaText,
      eventType: track.EventType.CLICK,
      eventUrl: window.location.href,
    })

    navigate(`${dueDateCalculatorPath}/${formatDueDate(dueDate)}`)
  }

  return (
    <>
      <div className={css.DueDateForm}>
        <div className={css.DueDateForm__backgroundWrapper}>
          <div className={css.DueDateForm__wrapper}>
            <div className={css.DueDateForm__content}>
              <h1>Pregnancy due date calculator</h1>
              <p>
                Find out when you'll welcome your new arrival(s) into the world!{' '}
                <span className={css.DueDateForm__desktopOnly}>
                  Enter the first day of your last menstrual period or
                  conception date and we'll show you your estimated due date.
                  Plus, explore the exciting milestones ahead for you and your
                  baby, including insights into their zodiac sign, birthstone
                  and more!
                </span>
              </p>
              <div className={css.DueDateForm__form}>
                <h2>Calculation method</h2>
                <Dropdown
                  className={css.DueDateForm__dropdown}
                  id="calculationMethod"
                  name="calculationMethod"
                  options={METHOD_OPTIONS}
                  size="lg"
                  value={calculationMethod}
                  onChange={(e) => setCalculationMethod(e as Option)}
                />
                <DatePickerInput
                  className={css.DueDateForm__datePicker}
                  handleChange={setSelectedDate}
                  initialValue={initialDate}
                  name="startDate"
                />
                {calculationMethod.value === 'Last day of period' && (
                  <>
                    <h2>Average cycle length (days)</h2>
                    <Dropdown
                      className={css.DueDateForm__dropdown}
                      options={CYCLE_LENGTH_OPTIONS}
                      size="lg"
                      value={cycleLength}
                      onChange={(e) => setCycleLength(e as Option)}
                    />
                  </>
                )}
                <Button
                  className={classNames(
                    css.DueDateForm__button,
                    css.DueDateForm__mobileOnly
                  )}
                  disabled={!isValid(selectedDate)}
                  size="md"
                  variant="primary"
                  onClick={handleSubmit}
                >
                  {ctaText}
                </Button>
                <Button
                  className={classNames(
                    css.DueDateForm__button,
                    css.DueDateForm__desktopOnly
                  )}
                  disabled={!isValid(selectedDate)}
                  size="lg"
                  variant="primary"
                  onClick={handleSubmit}
                >
                  {ctaText}
                </Button>
              </div>
            </div>
            <Background
              className={classNames(
                css.DueDateForm__background,
                css.DueDateForm__desktopOnly
              )}
            />
          </div>
        </div>
        {longForm && (
          <>
            <Wave className={css.DueDateForm__wave} />
            <FrequentlyAskedQuestions className={css.DueDateForm__faq} />
            <div className={css.DueDateForm__guidesWrapper}>
              <Guides posts={posts} />
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default DueDateForm
